import { useRef } from 'react';

import { Entry } from 'contentful';
import { NextPage } from 'next';

import { useRutilusQuery } from '../../api/graphql/useRutilusQuery';
import { BlogPostSection } from '../../components/Blog/BlogPostSection/BlogPostSection';
import { GQLFollowButton } from '../../components/Clickables/Buttons/GQLFollowButton';
import { Container } from '../../components/Container/Container';
import { Divider } from '../../components/Divider/Divider';
import { Grid } from '../../components/Grid/Grid';
import { Heading } from '../../components/Heading/Heading';
import { MetaTags } from '../../components/MetaTags/MetaTags';
import { MAIN_NAVIGATION_MIN_HEIGHT } from '../../components/Navigation/MainNavigation/constants/constants';
import { PageNavigation } from '../../components/Navigation/PageNavigation/PageNavigation';
import { PopularSpeciesNearYou } from '../../components/PopularSpeciesNearYou/PopularSpeciesNearYou';
import { PostsViewer } from '../../components/PostsViewer/PostsViewer';
import { openPostViewerEventSources } from '../../components/PostsViewer/types/openPostViewerEventSources';
import { Text } from '../../components/Text/Text';
import { useGeolocation } from '../../contexts/GeolocationContext/GeolocationContext';
import { getEdgesFromConnection, getNodesFromConnection } from '../../helpers/graphql';
import { abbreviateNumber } from '../../helpers/numbers/abbreviateNumber';
import { getSpeciesUrl } from '../../helpers/urls';
import { TypeBlogPostSkeleton } from '../../interfaces/contentful';
import {
  FollowRecordsTypeEnum,
  GetSingleSpecies_entry,
  GetSpeciesFromList_species_edges,
  GetTopSpeciesNearLocation,
  GetTopSpeciesNearLocationVariables,
  GetSingleSpecies_entry_catchesWithImages_edges_node as ISCatch,
  GetSingleSpecies_entry_speciesTips_edges_node as ISTip,
} from '../../interfaces/graphql';
import { getTopSpeciesNearLocation } from '../../queries/rutilus/Species';

import { usePostViewer } from '../../helpers/hooks/usePostViewer';
import { DetailedInformationSection } from './components/DetailedInformationSection';
import { HeaderImages } from './components/HeaderImages';
import { NearbyWatersSection } from './components/NearbyWatersSection';
import { NoSpeciesDataSection } from './components/NoSpeciesDataSection';
import { RecentCatchesSection } from './components/RecentCatchesSection';
import { TipsSection } from './components/TipsSection';
import { TopBaitsSection } from './components/TopBaitsSection';
import styles from './Species.module.css';
import { getSubNavLinks } from './speciesHelpers';

export interface ISpeciesProps {
  species: GetSingleSpecies_entry;
  defaultTopSpeciesEdgeList: GetSpeciesFromList_species_edges[];
  blogPostItems: Entry<TypeBlogPostSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>[] | null;
}

export const Species: NextPage<ISpeciesProps> = ({
  species,
  defaultTopSpeciesEdgeList,
  blogPostItems,
}) => {
  const postsReference = useRef<HTMLDivElement>(null);

  const {
    _id: species_id,
    catches,
    description,
    displayName,
    followers,
    latinName,
    exampleCatchWithImage,
    speciesTips,
    catchesWithImages,
    followedByCurrentUser,
  } = species;

  const catchList = getNodesFromConnection<ISCatch>(catchesWithImages);
  const recentCatches = catchList.slice(0, 6);
  const tipsList = getNodesFromConnection<ISTip>(speciesTips);

  const exampleImages = getNodesFromConnection(exampleCatchWithImage?.post.images);
  const exampleImage = exampleImages.length > 0 ? exampleImages[0] : undefined;

  const headingCatchList = exampleCatchWithImage
    ? [exampleCatchWithImage, ...recentCatches]
    : recentCatches;

  const {
    currentPostViewerId,
    setCurrentPostViewerId,
    isPostViewerOpen,
    openPostViewerEventSource,
    openPostViewer,
    closePostViewer,
  } = usePostViewer(postsReference, MAIN_NAVIGATION_MIN_HEIGHT + 4);
  const PostsViewerCatches =
    openPostViewerEventSource === openPostViewerEventSources.SpeciesHeaderImage
      ? headingCatchList.map(_catch => _catch.post)
      : catchList.map(_catch => _catch.post);

  /* Prevent scroll to behaviour for header cards */
  const handleClosePostsViewer =
    openPostViewerEventSource === openPostViewerEventSources.SpeciesHeaderImage
      ? async (): Promise<void> => closePostViewer(undefined)
      : closePostViewer;

  const loggedCatchesText = (): string => {
    if (catches.totalCount === 1) {
      return `${catches.totalCount} logged catch`;
    }
    const roundedCatchCount = abbreviateNumber(catches.totalCount);
    return `${roundedCatchCount} logged catches`;
  };

  const followerText = (): string => {
    if (followers.totalCount === 1) {
      return `${followers.totalCount} follower `;
    }
    const roundedFollowerCount = abbreviateNumber(followers.totalCount);
    return `${roundedFollowerCount} followers `;
  };

  const hasCatches = catchList.length > 0;
  const hasTips = tipsList.length > 0;

  const { isLoading: currentLocationLoading, location } = useGeolocation();

  const { data, loading } = useRutilusQuery<
    GetTopSpeciesNearLocation,
    GetTopSpeciesNearLocationVariables
  >(getTopSpeciesNearLocation, {
    variables: {
      first: 8,
      excludingSpeciesIds: [species_id],
      boundingCircle: {
        // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
        center: location?.coordinates!,
        delta: 50,
      },
    },
    skip: currentLocationLoading,
  });

  const topSpeciesEdgeList = data ? getEdgesFromConnection(data.topSpeciesNearLocation) : [];
  const POPULAR_SPECIES_SECTION_TITLE = 'Most popular fish species near you';

  return (
    <div ref={postsReference}>
      <PageNavigation navItems={getSubNavLinks({ hasCatches, hasTips })} />

      <Container className={styles.headerContainer}>
        <MetaTags
          title={`Fishing for ${displayName} near you`}
          link={`https://fishbrain.com${getSpeciesUrl(species_id, displayName)}`}
          description={`Discover popular ${displayName} fishing spots near you and learn the best tactics for increasing your catches. Find the best baits for ${displayName} based on real-user catches, updated frequently.`}
          image={exampleImage?.url}
        />
        <Grid className={styles.headerGrid} gapType="x" id="details-section">
          <div className={`${styles.headerTitle} mb-xs`}>
            <Heading as="h1" variant="xl" className="mt-0 mb-4">
              <Heading as="span" variant="2xs" className="text-midnight mt-0 mb-1 block">
                Fishing for{' '}
              </Heading>
              {displayName}
            </Heading>
            {latinName && (
              <Heading variant="xs" as="div" className="text-clownfish-shade mr-8">
                {latinName}
              </Heading>
            )}
            <div className="text-midnight mt-4">
              {loggedCatchesText()}
              <span>&nbsp;&bull;&nbsp;</span>
              {followerText()}
            </div>
            <div className="mt-6 md:mt-8">
              <GQLFollowButton
                source="species-page-follow-button"
                type={FollowRecordsTypeEnum.SPECIES}
                id={species_id}
                isFollowed={followedByCurrentUser}
                buttonProps={{ size: 'sm' }}
                customAppDownloadHeader={`Download the Fishbrain app to follow ${displayName}`}
              />
            </div>
          </div>

          <Text variant="lg" className={`${styles.headerDescription} mb-sm whitespace-pre-wrap`}>
            {description}
          </Text>

          <HeaderImages
            exampleCatchWithImage={exampleCatchWithImage}
            recentCatches={recentCatches}
            displayName={displayName}
            openPostViewer={openPostViewer}
          />

          <div className={`${styles.headerInfo} mb-md`}>
            <DetailedInformationSection species={species} />
          </div>

          {catchList.length > 0 && (
            <div className={styles.headerBaits} id="top-baits-section">
              <TopBaitsSection speciesId={species_id} speciesName={displayName} />
            </div>
          )}

          {tipsList.length > 0 && (
            <div className={`${styles.headerTips} mt-md`} id="tips-section">
              <TipsSection tipsList={tipsList} />
            </div>
          )}
        </Grid>

        <Divider variant="md" />
      </Container>

      {hasCatches ? (
        <>
          <div className="mb-md col-span-4 sm:col-span-12" id="nearby-waters-section">
            <NearbyWatersSection speciesId={species_id} speciesName={displayName} />
          </div>

          <Container className="mb-sm">
            <RecentCatchesSection
              catchList={catchList}
              displayName={displayName}
              openPostViewer={openPostViewer}
            />
          </Container>

          {blogPostItems && blogPostItems.length > 0 && (
            <BlogPostSection
              blogPostItems={blogPostItems}
              linkTo="/blog/species"
              speciesId={species_id}
            />
          )}
          <PopularSpeciesNearYou
            sectionTitle={POPULAR_SPECIES_SECTION_TITLE}
            clickEventSource="species-other-top-species"
            clickEventSourceId={species_id}
            topSpeciesEdgeList={topSpeciesEdgeList}
            defaultSpeciesEdgeList={defaultTopSpeciesEdgeList}
            isLoading={loading || currentLocationLoading}
          />
        </>
      ) : (
        <NoSpeciesDataSection id={species_id} topSpeciesEdgeList={topSpeciesEdgeList} />
      )}

      {isPostViewerOpen && currentPostViewerId && (
        <PostsViewer
          posts={PostsViewerCatches}
          currentPostViewerId={currentPostViewerId}
          hasMore={false}
          isPostViewerOpen
          loadMore={() => {}}
          onClosePostsViewer={handleClosePostsViewer}
          setCurrentPostViewerId={setCurrentPostViewerId}
        />
      )}
    </div>
  );
};
